import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import type { RolloutDayKey, RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import {
  useAddRolloutDraftsMutation,
  useMakePlanStepReelMutation,
  useMovePromoPostMutation,
  useSetPlanChannelsMutation,
  useSetPlanStepChannelsMutation,
  useSkipPlanStepMutation,
  useSwapPlanStepMediaMutation,
  useUnskipPlanStepMutation,
} from '../../../../../../redux/api/fymAPI';
import { SocialPlatform } from '../../../types';
import { useVideoCreditGate } from '../../../credits/useVideoCreditGate';
import {
  StudioActionFeedback,
  studioActionErrorFeedback,
  studioErrorCode,
} from '../../../studio/studioActionErrorText';
import { usePostsLaunch, type LaunchRun } from './launch/usePostsLaunch';
import { rolloutMessages } from './rolloutMessages';

/** The countdown steps: one story run makes all three (owner decision 3). */
export const COUNTDOWN_KEYS: RolloutDayKey[] = ['countdown7', 'countdown3', 'countdown1'];

/** The refusal of a draft with no channel (music24-api `KitRequestError`). */
const ROLLOUT_NO_CHANNELS = 'M24_ROLLOUT_NO_CHANNELS';

function noChannelsFeedback(intl: ReturnType<typeof useIntl>): StudioActionFeedback {
  return { tone: 'danger', message: intl.formatMessage(rolloutMessages.noChannelsError) };
}

/** The video credits of an AI reel made from the plan (owner decision 4). */
export const PLAN_REEL_CREDITS = 1;

/**
 * The actions of the rollout run sheet (design rollout-plan a1–e1). Each action keeps its own
 * result line, keyed by the place that shows it (`plan`, `step:<key>`, `sheet`, `needs`), so the
 * sentence sits next to its button (owner rule 2026-10-06: never a toast). A 402 opened the plan or
 * credit prompt already (`customBaseQuery`), so it shows no line.
 *
 * `kind` is the plan that the steps belong to: `release` (the default) or `reels`, the run sheet of
 * an AI reels plan. For `reels`, `makeStepReel` is "Make it again" of a step whose reel failed.
 */
export function useRolloutActions(input: {
  accountId: number;
  catalogItemUuid: string;
  platforms: SocialPlatform[];
  kind?: RolloutKind;
}) {
  const { accountId, catalogItemUuid, platforms, kind = 'release' } = input;
  const intl = useIntl();
  const base = useMemo(
    () => ({ accountId, catalogItemUuid, kind, platforms }),
    [accountId, catalogItemUuid, kind, platforms]
  );
  const [feedback, setFeedback] = useState<Record<string, StudioActionFeedback | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const { canSpend } = useVideoCreditGate(accountId);

  const [addDrafts] = useAddRolloutDraftsMutation();
  const [makeReel] = useMakePlanStepReelMutation();
  const [movePost] = useMovePromoPostMutation();
  const [setChannels] = useSetPlanChannelsMutation();
  const [setStepChannels] = useSetPlanStepChannelsMutation();
  const [skipStep] = useSkipPlanStepMutation();
  const [unskipStep] = useUnskipPlanStepMutation();
  const [swapMedia] = useSwapPlanStepMediaMutation();

  const run = useCallback(
    async (place: string, action: string, work: () => Promise<string | null>): Promise<boolean> => {
      setBusy(place);
      setFeedback((current) => ({ ...current, [place]: null }));
      try {
        const info = await work();
        setFeedback((current) => ({ ...current, [place]: info === null ? null : { tone: 'info', message: info } }));
        return true;
      } catch (caught: unknown) {
        if (studioErrorCode(caught) === ROLLOUT_NO_CHANNELS) {
          setFeedback((current) => ({ ...current, [place]: noChannelsFeedback(intl) }));
        } else if (!(caught instanceof FYMAPIError && caught.statusCode === 402)) {
          const sentence = studioActionErrorFeedback(intl, action, caught);
          setFeedback((current) => ({ ...current, [place]: sentence }));
        }
        return false;
      } finally {
        setBusy(null);
      }
    },
    [intl]
  );

  /**
   * "Plan all N posts" (no `dayKeys`) or "Plan this step": drafts, no quota (owner decision 1).
   * With no channel it sends nothing and says what to do (QA 2026-10-28, imp-1: an empty list
   * answered 400 and a general error).
   */
  const planDays = useCallback(
    (place: string, dayKeys?: RolloutDayKey[]) => {
      if (platforms.length === 0) {
        setFeedback((current) => ({ ...current, [place]: noChannelsFeedback(intl) }));
        return Promise.resolve(false);
      }
      return run(place, 'planRollout', async () => {
        const answer = await addDrafts({ ...base, dayKeys }).unwrap();
        return intl.formatMessage(rolloutMessages.addedResult, { count: answer.created });
      });
    },
    [addDrafts, base, intl, platforms.length, run]
  );

  /**
   * "Make the countdowns" and "Make it" of a missing kit asset: the asset is queued, and its drafts
   * come when it is ready (owner decision 3). The images are free.
   */
  const makeAndPlan = useCallback(
    (place: string, dayKeys: RolloutDayKey[]) =>
      run(place, 'makeRolloutAsset', async () => {
        await addDrafts({ ...base, dayKeys, makeMissing: true, autoDraft: true }).unwrap();
        return null;
      }),
    [addDrafts, base, run]
  );

  /** "Make an AI reel" of a step: the credit gate first, then the reel with the default settings. */
  const makeStepReel = useCallback(
    (place: string, dayKey: RolloutDayKey) => {
      if (!canSpend(PLAN_REEL_CREDITS)) {
        return Promise.resolve(false);
      }
      return run(place, 'makeRolloutReel', async () => {
        await makeReel({ ...base, dayKey }).unwrap();
        return null;
      });
    },
    [base, canSpend, makeReel, run]
  );

  /**
   * "Schedule all N posts", "Schedule" of one step and of one post: one flow, shared with the
   * Calendar (`usePostsLaunch`). The result is `launchOutcome`, never one error.
   */
  const runLaunch = useCallback<LaunchRun>(
    (place, _stage, work) =>
      run(place, 'scheduleRolloutPosts', async () => {
        await work();
        return null;
      }),
    [run]
  );
  const { launch, answerLaunch, cancelLaunch, dismissLaunchOutcome, pendingLaunch, launchOutcome } = usePostsLaunch({
    accountId,
    run: runLaunch,
  });

  const movePostTo = useCallback(
    (place: string, postUuid: string, scheduledAt: string) =>
      run(place, 'moveRolloutPost', async () => {
        await movePost({ accountId, postUuid, scheduledAt }).unwrap();
        return null;
      }),
    [accountId, movePost, run]
  );

  const setDefaultChannels = useCallback(
    (place: string, channels: SocialPlatform[]) =>
      run(place, 'setRolloutChannels', async () => {
        await setChannels({ ...base, channels }).unwrap();
        return null;
      }),
    [base, run, setChannels]
  );

  const setChannelsOfStep = useCallback(
    (place: string, dayKey: RolloutDayKey, channels: SocialPlatform[]) =>
      run(place, 'setRolloutStepChannels', async () => {
        await setStepChannels({ ...base, dayKey, channels }).unwrap();
        return null;
      }),
    [base, run, setStepChannels]
  );

  const skip = useCallback(
    (place: string, dayKey: RolloutDayKey) =>
      run(place, 'skipRolloutStep', async () => {
        await skipStep({ ...base, dayKey }).unwrap();
        return null;
      }),
    [base, run, skipStep]
  );

  const unskip = useCallback(
    (place: string, dayKey: RolloutDayKey) =>
      run(place, 'unskipRolloutStep', async () => {
        await unskipStep({ ...base, dayKey }).unwrap();
        return null;
      }),
    [base, run, unskipStep]
  );

  const swap = useCallback(
    (place: string, dayKey: RolloutDayKey, fileId: number) =>
      run(place, 'swapRolloutMedia', async () => {
        await swapMedia({ ...base, dayKey, fileId }).unwrap();
        return null;
      }),
    [base, run, swapMedia]
  );

  return {
    feedback,
    busy,
    planDays,
    makeAndPlan,
    makeStepReel,
    launch,
    answerLaunch,
    cancelLaunch,
    dismissLaunchOutcome,
    pendingLaunch,
    launchOutcome,
    movePostTo,
    setDefaultChannels,
    setChannelsOfStep,
    skip,
    unskip,
    swap,
  };
}

export type RolloutActions = ReturnType<typeof useRolloutActions>;
